import React, { useEffect, useState } from "react"

type PaymentSource = "paypal" | "card" | "apple_pay" | "google-pay"

interface ButtonProps {
  createOrder: (data: any, source: PaymentSource) => Promise<any>
  onApprove: (data: any, source: PaymentSource) => Promise<any>
  isSdkReady: boolean
  enabled?: boolean
  orderTotalRef: React.MutableRefObject<number>
}

export const PayPalGooglePay: React.FC<ButtonProps> = ({
  createOrder,
  onApprove,
  isSdkReady,
  enabled = true,
  orderTotalRef,
}) => {
  const [googleReady, setGoogleReady] = useState(false)
  const [paymentsClient, setPaymentsClient] = useState<any>(null)
  const total = Number(orderTotalRef.current || orderTotalRef || 0)

  const baseRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
  }

  const getGooglePayConfig = async () => {
    const googlePayConfig = await window.paypal.Googlepay().config()

    if (!googlePayConfig.isEligible) {
      console.warn("Google Pay is not eligible on this device.")
      return null
    }
    return {
      allowedPaymentMethods: googlePayConfig.allowedPaymentMethods,
      merchantInfo: googlePayConfig.merchantInfo,
    }
  }

  const getGooglePaymentsClient = () => {
    if (!paymentsClient) {
      const client = new window.google.payments.api.PaymentsClient({
        environment: "TEST",
        paymentDataCallbacks: { onPaymentAuthorized },
      })
      setPaymentsClient(client)
      return client
    }
    return paymentsClient
  }

  const getGoogleTransactionInfo = () => ({
    countryCode: "US",
    currencyCode: "USD",
    totalPriceStatus: "FINAL",
    totalPrice: total.toFixed(2),
    totalPriceLabel: "Total",
  })

  const getGooglePaymentDataRequest = async () => {
    const config = await getGooglePayConfig()
    if (!config) return null

    const { allowedPaymentMethods, merchantInfo } = config

    return {
      ...baseRequest,
      allowedPaymentMethods,
      merchantInfo,
      transactionInfo: getGoogleTransactionInfo(),
      callbackIntents: ["PAYMENT_AUTHORIZATION"],
    }
  }

  const onPaymentAuthorized = async (paymentData: any) => {
    try {
      const { currencyCode, totalPrice } = getGoogleTransactionInfo()
      const order = {
        intent: "CAPTURE",
        purchase_units: [
          {
            amount: {
              currency_code: currencyCode,
              value: totalPrice,
            },
          },
        ],
      }

      const id = await createOrder(order, "google-pay")

      const { status } = await window.paypal.Googlepay().confirmOrder({
        orderId: id,
        paymentMethodData: paymentData.paymentMethodData,
      })

      if (status === "APPROVED") {
        await onApprove({ orderID: id }, "google-pay")
        return { transactionState: "SUCCESS" }
      } else {
        return { transactionState: "ERROR" }
      }
    } catch (err: any) {
      return {
        transactionState: "ERROR",
        error: { message: err.message },
      }
    }
  }

  const addGooglePayButton = async () => {
    const client = getGooglePaymentsClient()
    const button = client.createButton({
      onClick: async () => {
        const req = await getGooglePaymentDataRequest()
        if (req) client.loadPaymentData(req)
      },
      buttonColor: "black",
      buttonType: "pay",
      buttonSizeMode: "fill",
    })

    const container = document.getElementById("google-pay-button-container")
    if (container) {
      container.innerHTML = ""
      container.appendChild(button)
    }
  }

  useEffect(() => {
    if (!isSdkReady || !enabled) return

    if (!document.getElementById("google-pay-sdk")) {
      const script = document.createElement("script")
      script.id = "google-pay-sdk"
      script.src = "https://pay.google.com/gp/p/js/pay.js"
      script.async = true
      script.onload = () => setGoogleReady(true)
      document.body.appendChild(script)
    } else {
      setGoogleReady(true)
    }
  }, [isSdkReady, enabled])

  useEffect(() => {
    if (googleReady && window.google && window.paypal?.Googlepay) {
      addGooglePayButton()
    }
  }, [googleReady])

  useEffect(() => {
    if (googleReady) {
      addGooglePayButton() // Rebuild button when total changes (Promo added)
    }
  }, [total])

  return (
    <div
      id="google-pay-button-container"
      className="w-full flex flex-col items-stretch"
    />
  )
}
